/**
 * The `site-publish` card (plan 6.2, 7.6) as data, shared by the web and
 * native cards. `publishArtifact` in chat never publishes: it returns
 * `open_publish_sheet` with a suggested address, and the card's Publish opens
 * the sheet prefilled. `unpublishArtifact` (and an approved MCP publish)
 * return `published` or `unpublished` with the site. A refusal comes back as
 * `{ ok: false, error }` with plain words in the user's language.
 */
import type { PublishedSiteSummary } from '@tabbio/types';
import { isSiteLive, siteHost } from '../../sites/publish-sheet-model';

export type SitePublishCardModel =
  | {
      kind: 'sheet';
      artifactId: string;
      suggestedName: string | null;
      /** `<suggested>.tabbio.site` */
      host: string | null;
      site: PublishedSiteSummary | null;
    }
  | {
      kind: 'changed';
      action: 'published' | 'unpublished';
      artifactId: string;
      site: PublishedSiteSummary | null;
    }
  | { kind: 'error'; message: string };

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readSite(value: unknown): PublishedSiteSummary | null {
  const site = record(value);
  if (!site || typeof site.id !== 'string' || typeof site.artifactId !== 'string') return null;
  return site as unknown as PublishedSiteSummary;
}

export function parseSitePublishResult(result: unknown): SitePublishCardModel | null {
  const source = record(result);
  if (!source) return null;
  if (source.ok === false) {
    const error = record(source.error);
    return { kind: 'error', message: typeof error?.message === 'string' ? error.message : '' };
  }
  const artifactId = typeof source.artifactId === 'string' ? source.artifactId : '';
  if (!artifactId) return null;
  if (source.action === 'open_publish_sheet') {
    const suggestedName = typeof source.suggestedName === 'string' && source.suggestedName ? source.suggestedName : null;
    return {
      kind: 'sheet',
      artifactId,
      suggestedName,
      host: typeof source.url === 'string' && source.url ? siteHost(source.url) : null,
      site: readSite(source.site),
    };
  }
  if (source.action === 'published' || source.action === 'unpublished') {
    return { kind: 'changed', action: source.action, artifactId, site: readSite(source.site) };
  }
  return null;
}

export type SitePublishCardView = {
  /** Copy key and params of the meta line. */
  meta: { key: string; params?: Record<string, string> };
  /** The card's one action. */
  action: 'publish' | 'manage' | 'publishAgain' | null;
  live: boolean;
};

/**
 * What the card says now: the fresher site (the artifact's own, after the
 * sheet published it) wins over the one in the tool result.
 */
export function sitePublishCardView(
  model: SitePublishCardModel,
  fresh: Pick<PublishedSiteSummary, 'status' | 'name' | 'url'> | null | undefined,
): SitePublishCardView {
  if (model.kind === 'error') {
    return { meta: { key: model.message ? '' : 'chat.artifacts.publish.failed' }, action: null, live: false };
  }
  const site = fresh === undefined ? model.site : fresh;
  if (isSiteLive(site)) {
    return {
      meta: { key: 'chat.artifacts.publish.publishedAt', params: { host: siteHost(site?.url) } },
      action: 'manage',
      live: true,
    };
  }
  if (model.kind === 'changed' && model.action === 'unpublished') {
    return { meta: { key: 'chat.artifacts.siteCard.unpublished' }, action: 'publishAgain', live: false };
  }
  if (model.kind === 'sheet') {
    return model.host
      ? { meta: { key: 'chat.artifacts.siteCard.ready' }, action: 'publish', live: false }
      : { meta: { key: 'chat.artifacts.siteCard.noName' }, action: 'publish', live: false };
  }
  // Published over MCP but no longer live (unpublished since).
  return { meta: { key: 'chat.artifacts.siteCard.unpublished' }, action: 'publishAgain', live: false };
}
